import { z } from 'zod';

export const visibilitySchema = z.enum(['private', 'shared']);

/**
 * タグ配列のサニタイズ + サイズ制約。trim + 重複除去 + 空除去 + 32文字以内 + 最大10個。
 *
 * マスタとの整合性（tags コレクションに存在する name のみ受付）は **UI 層**
 * （`src/components/compose/TagInput.tsx`）で担保する。Zod は同期処理向きで
 * Firestore からマスタを引いて検証するのに向かないため、ここは緩めの形式チェックのみ。
 *
 * 編集モードで過去のマスタ外タグが残っている可能性があるため、スキーマレベルで
 * マスタ存在を強制すると編集自体が壊れる。UI で「マスタ外」セクションを表示し、
 * × で外すか保存するかをユーザに選ばせる方針。
 */
export const tagsSchema = z
  .array(z.string())
  .transform((arr) =>
    Array.from(new Set(arr.map((s) => s.trim()).filter(Boolean))),
  )
  .pipe(z.array(z.string().max(32)).max(10));

/** http/https のみ許可（javascript: 等は拒否） */
export const safeUrlSchema = z
  .string()
  .trim()
  .min(1, 'URL を入力してください')
  .max(2000)
  .refine((v) => {
    try {
      const u = new URL(v);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  }, 'URL の形式が正しくありません（http:// か https:// のみ）');

export const titleSchema = z
  .string()
  .trim()
  .min(1, 'タイトルを入力してください')
  .max(200, 'タイトルは200文字以内');

export const longTextSchema = (max: number, label = '本文') =>
  z.string().max(max, `${label}は${max}文字以内`);

/**
 * 投稿の projectId 形式制約。Firestore addDoc 自動生成 ID は 20 字 base57 相当だが
 * 互換と将来的なカスタム ID 対応のため [A-Za-z0-9_-]{1,40} を許容。
 *
 * Rules では projectId の整合性検証を行わない（UI 層担保、tags と同パターン）。
 * このスキーマは「巨大 string 投入による DoS の予防」+「CWE-20 の最低限の入力検証」が目的。
 */
export const projectIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]{1,40}$/, 'トピック ID の形式が正しくありません')
  .optional();

export function csvToTags(input: string): string[] {
  return input
    .split(/[,、]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 10);
}
